import { useEffect, useRef, useState } from "react";
import { AreaChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, ComposedChart, Legend } from "recharts";
import { api, fmtMoney } from "@/lib/api";
import MiniSparklineSVG from "@/components/MiniSparklineSVG";
import BotPLDrilldown from "@/components/BotPLDrilldown";

const RANGES = [
  { label: "7D", days: 7, period: "1mo" },
  { label: "30D", days: 30, period: "3mo" },
  { label: "90D", days: 90, period: "6mo" },
  { label: "1Y", days: 365, period: "1y" },
];

export default function PortfolioValueChart({ startingCash = 100000 }) {
  const [series, setSeries] = useState([]);
  const [byBot, setByBot] = useState([]);
  const [perBotSeries, setPerBotSeries] = useState([]);
  const [range, setRange] = useState(RANGES[1]);
  const [loading, setLoading] = useState(false);
  const [showSP, setShowSP] = useState(true);
  // Iter 79 — Realized P/L Line. Persisted toggle so admins can dim
  // the amber realized-P/L overlay for a cleaner screenshot without
  // losing their choice on the next visit.
  const [showRealized, setShowRealized] = useState(() => {
    try {
      const v = localStorage.getItem("stratos.chart.show-realized");
      return v === null ? true : v === "1";
    } catch { return true; }
  });
  // Iter 82 — Bot Comparison Overlay. Persist so heavy users don't
  // have to re-enable every visit.
  const [showCompare, setShowCompare] = useState(() => {
    try {
      const v = localStorage.getItem("stratos.chart.compare-bots");
      return v === "1";
    } catch { return false; }
  });
  // Iter 86 — Legend Arrow Loop Toast. When arrow-Right wraps
  // last→first (or arrow-Left first→last) the target chip briefly
  // flashes its border amber so users see the wrap happened.
  // Iter 87 — Legend Flash Persist. When a second wrap fires while
  // the 300ms amber flash is still running, extend the existing
  // animation (clear + restart the timer with the same idx) so
  // the ring stays lit continuously instead of blinking off/on
  // mid-frame.
  const [wrapFlashIdx, setWrapFlashIdx] = useState(null);
  const wrapFlashTimerRef = useRef(null);
  useEffect(() => () => {
    if (wrapFlashTimerRef.current) {
      clearTimeout(wrapFlashTimerRef.current);
      wrapFlashTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/portfolio/history?days=${range.days}`),
      api.get(`/market/history/SPY?period=${range.period}&interval=1d`).catch(() => ({ data: { bars: [] } })),
    ]).then(([pRes, sRes]) => {
      const pf = pRes.data.series || [];
      const spyBars = sRes.data.bars || [];
      // Build SPY index of {date -> close}
      const spyMap = {};
      spyBars.forEach(b => { spyMap[b.t.slice(0, 10)] = b.c; });
      // Normalize SPY so its first point on our series matches startingCash
      let firstSpy = null;
      const merged = pf.map(pt => {
        const spyClose = spyMap[pt.t];
        if (spyClose != null && firstSpy == null) firstSpy = spyClose;
        const spyNorm = spyClose != null && firstSpy != null
          ? (spyClose / firstSpy) * startingCash
          : null;
        return {
          t: pt.t,
          equity: pt.equity,
          spy: spyNorm,
          // Iter 79 — Realized P/L Line. Backend now returns
          // cumulative closed-trade P/L per day. Offset by
          // startingCash so the line shares the equity Y-axis
          // without needing a secondary scale.
          realized_pl: pt.realized_pl ?? 0,
          realized_baseline: startingCash + (pt.realized_pl ?? 0),
        };
      });
      // Iter 82 — Bot Comparison Overlay. Merge top-2 bot
      // cumulative curves onto each series point so recharts can
      // render them alongside the aggregate.
      const perBot = pRes.data.per_bot_series || [];
      const byDayByBot = perBot.map((entry) => {
        const map = {};
        (entry.series || []).forEach((p) => { map[p.t] = p.cum; });
        return { ...entry, map };
      });
      merged.forEach((pt) => {
        byDayByBot.forEach((entry, i) => {
          const cum = entry.map[pt.t] ?? 0;
          pt[`bot${i + 1}_baseline`] = startingCash + cum;
          pt[`bot${i + 1}_cum`] = cum;
        });
      });
      // Forward-fill any missing SPY values (weekends etc.)
      let last = null;
      for (const pt of merged) {
        if (pt.spy != null) last = pt.spy;
        else if (last != null) pt.spy = last;
      }
      setSeries(merged);
      // Iter 80 — Bot P/L Attribution. Response now includes
      // `realized_pl_by_bot` — ranked, non-zero contributors.
      setByBot(pRes.data.realized_pl_by_bot || []);
      // Iter 82 — top-2 per-bot cumulative curves.
      setPerBotSeries(pRes.data.per_bot_series || []);
    }).finally(() => setLoading(false));
  }, [range, startingCash]);

  const last = series[series.length - 1]?.equity;
  const first = series[0]?.equity ?? startingCash;
  const change = (last ?? 0) - first;
  const up = change >= 0;
  const color = up ? "#00C805" : "#FF3B30";

  const spyLast = series[series.length - 1]?.spy;
  const spyReturn = spyLast != null ? ((spyLast - startingCash) / startingCash) * 100 : null;
  const baseFirst = first || startingCash || 1;
  const myReturn = ((last ?? startingCash) - baseFirst) / baseFirst * 100;
  const beatingSP = spyReturn != null && myReturn > spyReturn;
  // Iter 79 — Realized P/L Line. Latest booked P/L (>=0 → green
  // profit chip, <0 → red loss chip) so admins see the summary
  // number even when the toggle is off.
  const realizedLast = series[series.length - 1]?.realized_pl ?? 0;
  const realizedUp = realizedLast >= 0;

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
        <div>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Portfolio value</div>
          <div className="mono text-2xl font-bold mt-1">{last != null ? fmtMoney(last) : "—"}</div>
          <div className="mono text-xs mt-0.5 flex items-center gap-3">
            <span className={up ? "profit" : "loss"}>{up ? "+" : ""}{fmtMoney(change)} · {range.label}</span>
            {spyReturn != null && (
              <span className={`px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-widest ${
                beatingSP ? "border-[#00C805] text-[#00C805]" : "border-zinc-700 text-zinc-400"
              }`} data-testid="sp-benchmark-badge">
                {beatingSP ? "Beating" : "Trailing"} S&P {(myReturn - spyReturn).toFixed(2) + "%"}
              </span>
            )}
            {/* Iter 79 — Realized P/L Line. Small tabular chip that
                mirrors the amber overlay's endpoint so admins get the
                summary number even when the overlay is off.
                Iter 80 — chip now enriched with the top-contributor
                bot ("$X from MACD-crossover · +N more") and a
                paired inline sparkline showing the *shape* of the
                cumulative realized P/L series. */}
            {(() => {
              const topBot = byBot.length ? byBot[0] : null;
              const moreCount = Math.max(0, byBot.length - 1);
              const chipTitle = topBot
                ? `Cumulative realized P/L across closed trades in the last ${range.label} · top: ${topBot.bot_name} ${topBot.realized_pl >= 0 ? "+" : ""}${fmtMoney(topBot.realized_pl)}${moreCount ? ` · +${moreCount} more` : ""}`
                : `Cumulative realized P/L across closed trades in the last ${range.label}`;
              // Iter 80 — Realized P/L Sparkline. 24-point rolling
              // slice of the cumulative series so users see the
              // shape (steady climb vs one big win) at a glance.
              const realizedSeries = series.map((p) => p.realized_pl ?? 0);
              return (
                <span
                  data-testid="realized-pl-chip"
                  data-value={realizedLast}
                  data-tone={realizedUp ? "profit" : "loss"}
                  data-top-bot={topBot ? (topBot.bot_name || "") : ""}
                  data-bot-count={byBot.length}
                  className={`px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-widest tabular-nums inline-flex items-center gap-1.5 ${
                    realizedUp
                      ? "border-amber-500/60 text-amber-300 bg-amber-500/5"
                      : "border-red-500/60 text-red-300 bg-red-500/5"
                  }`}
                  title={chipTitle}
                >
                  <span>Realized {realizedUp ? "+" : ""}{fmtMoney(realizedLast)}</span>
                  {realizedSeries.length >= 2 && (
                    <span data-testid="realized-pl-chip-sparkline" className="inline-flex items-center">
                      <MiniSparklineSVG
                        values={realizedSeries}
                        up={realizedUp}
                        width={36}
                        height={12}
                        ariaLabel="Cumulative realized P/L trend"
                      />
                    </span>
                  )}
                  {topBot && (
                    <BotPLDrilldown botId={topBot.bot_id} botName={topBot.bot_name}>
                      <span
                        data-testid="realized-pl-chip-top-bot"
                        className="normal-case tracking-normal text-[9px] text-amber-200/80 hover:text-amber-100 underline decoration-dotted underline-offset-2"
                      >
                        · {topBot.bot_name}
                        {moreCount > 0 && (
                          <span className="text-amber-200/50"> +{moreCount}</span>
                        )}
                      </span>
                    </BotPLDrilldown>
                  )}
                </span>
              );
            })()}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              const next = !showRealized;
              setShowRealized(next);
              try { localStorage.setItem("stratos.chart.show-realized", next ? "1" : "0"); } catch {}
            }}
            data-testid="toggle-realized-overlay"
            data-active={showRealized ? "true" : "false"}
            className={`mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-sm border ${
              showRealized ? "border-amber-500 bg-amber-500/10 text-amber-300" : "border-zinc-800 text-zinc-500 hover:text-white"
            }`}
            title="Toggle the amber cumulative realized-P/L overlay"
          >
            Realized P/L
          </button>
          {/* Iter 82 — Bot Comparison Overlay toggle. Only enabled
              when the backend actually returned per-bot series
              (needs ≥2 non-manual bots with realized P/L). */}
          {perBotSeries.length >= 2 && (
            <button
              onClick={() => {
                const next = !showCompare;
                setShowCompare(next);
                try { localStorage.setItem("stratos.chart.compare-bots", next ? "1" : "0"); } catch {}
              }}
              data-testid="toggle-compare-bots"
              data-active={showCompare ? "true" : "false"}
              data-bot-count={perBotSeries.length}
              className={`mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-sm border ${
                showCompare ? "border-violet-500 bg-violet-500/10 text-violet-300" : "border-zinc-800 text-zinc-500 hover:text-white"
              }`}
              title={`Overlay top ${perBotSeries.length} bots' cumulative P/L curves`}
            >
              Compare bots
            </button>
          )}
          <button
            onClick={() => setShowSP(!showSP)}
            data-testid="toggle-sp-overlay"
            className={`mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-sm border ${
              showSP ? "border-white bg-white text-black" : "border-zinc-800 text-zinc-500 hover:text-white"
            }`}
          >
            vs S&P 500
          </button>
          <div className="flex gap-1">
            {RANGES.map((r) => (
              <button
                key={r.label}
                data-testid={`portfolio-range-${r.label}`}
                onClick={() => setRange(r)}
                className={`px-2.5 py-1 text-[10px] mono uppercase tracking-widest rounded-sm border ${
                  range.label === r.label
                    ? "border-white bg-white text-black"
                    : "border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-600"
                }`}
              >{r.label}</button>
            ))}
          </div>
        </div>
      </div>
      {/* Iter 83 — Compare Bots Legend. Renders below the header row
          when the compare overlay is on so users can identify which
          curve is which without hovering the chart. */}
      {showCompare && perBotSeries.length >= 1 && (
        <div
          data-testid="compare-bots-legend"
          data-count={Math.min(2, perBotSeries.length)}
          data-legend-nav-root="true"
          className="px-3 pb-2 flex items-center flex-wrap gap-3 text-[10px] mono uppercase tracking-widest text-zinc-500"
        >
          <span>Overlay</span>
          {perBotSeries.slice(0, 2).map((b, i) => {
            const cum = b.series?.length ? (b.series[b.series.length - 1].cum ?? 0) : 0;
            const colour = i === 0 ? "#FBBF24" : "#A78BFA";
            const toneCls = i === 0 ? "text-amber-300" : "text-violet-300";
            const sign = cum >= 0 ? "+" : "";
            // Iter 84 — Legend Click Focus. Wrap each item in its
            // own BotPLDrilldown so clicking a legend chip opens
            // the per-bot trade list. Turns the legend into a nav
            // shortcut without disturbing the on-chart curves.
            // Iter 85 — Legend Keyboard Nav. Arrow left/right (and
            // Home/End) cycle focus between the two chips so
            // keyboard users can jump between drilldowns without
            // Tab-crossing the chart.
            return (
              <BotPLDrilldown key={b.bot_id || i} botId={b.bot_id} botName={b.bot_name}>
                <span
                  data-testid={`compare-bots-legend-item-${i + 1}`}
                  data-bot-id={b.bot_id}
                  data-cum={cum}
                  data-legend-nav={i + 1}
                  tabIndex={0}
                  role="button"
                  onKeyDown={(e) => {
                    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
                    e.preventDefault();
                    const root = e.currentTarget.closest("[data-legend-nav-root]");
                    if (!root) return;
                    const items = Array.from(root.querySelectorAll("[data-legend-nav]"));
                    const idx = items.indexOf(e.currentTarget);
                    if (idx < 0) return;
                    let nextIdx = idx;
                    let wrapped = false;
                    if (e.key === "ArrowLeft") {
                      nextIdx = (idx - 1 + items.length) % items.length;
                      wrapped = idx === 0 && items.length > 1;
                    } else if (e.key === "ArrowRight") {
                      nextIdx = (idx + 1) % items.length;
                      wrapped = idx === items.length - 1 && items.length > 1;
                    } else if (e.key === "Home") nextIdx = 0;
                    else if (e.key === "End") nextIdx = items.length - 1;
                    items[nextIdx].focus();
                    // Iter 86 — flash the target chip for 300ms on wrap.
                    // Iter 87 — Flash Persist. If a flash is already
                    // in-flight, clear the running timer and start a
                    // fresh 300ms window so the ring stays lit
                    // continuously instead of blinking off/on. Same
                    // idx keeps the CSS class stable, so the flash
                    // "extends" cleanly across the boundary.
                    if (wrapped) {
                      const targetOneBased = nextIdx + 1;
                      if (wrapFlashTimerRef.current) {
                        clearTimeout(wrapFlashTimerRef.current);
                        wrapFlashTimerRef.current = null;
                      }
                      setWrapFlashIdx(targetOneBased);
                      wrapFlashTimerRef.current = setTimeout(() => {
                        wrapFlashTimerRef.current = null;
                        setWrapFlashIdx((cur) => (cur === targetOneBased ? null : cur));
                      }, 300);
                    }
                  }}
                  data-wrap-flash={wrapFlashIdx === i + 1 ? "true" : "false"}
                  className={`inline-flex items-center gap-1.5 normal-case tracking-normal cursor-pointer hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded-sm px-1 transition-[box-shadow] duration-300 ${
                    wrapFlashIdx === i + 1
                      ? "ring-2 ring-amber-400 shadow-[0_0_0_2px_rgba(251,191,36,0.35)]"
                      : ""
                  }`}
                  title={`${b.bot_name} · cumulative ${sign}${fmtMoney(cum)} across the last ${range.label} · click to view individual sells`}
                >
                  <span
                    aria-hidden="true"
                    className="inline-block w-4 h-[2px]"
                    style={{
                      backgroundImage: `linear-gradient(90deg, ${colour} 55%, transparent 55%)`,
                      backgroundSize: "6px 2px",
                    }}
                  />
                  <span className={`text-[10px] ${toneCls}`}>{b.bot_name}</span>
                  <span className={`tabular-nums text-[10px] ${toneCls}/70`}>{sign}{fmtMoney(cum)}</span>
                </span>
              </BotPLDrilldown>
            );
          })}
        </div>
      )}
      <div className="flex-1 p-2 min-h-[240px]">
        {loading || series.length === 0 ? (
          <div className="h-full flex items-center justify-center text-zinc-600 mono text-xs">
            {loading ? "Loading…" : "No data"}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="pfill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="t"
                tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "#71717a" }}
                axisLine={{ stroke: "#27272a" }}
                tickLine={false}
                tickFormatter={(v) => { const d = new Date(v); return `${d.getMonth() + 1}/${d.getDate()}`; }}
                minTickGap={40}
              />
              <YAxis
                domain={["auto", "auto"]}
                tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "#71717a" }}
                axisLine={{ stroke: "#27272a" }}
                tickLine={false}
                orientation="right"
                width={60}
                tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`}
              />
              <Tooltip
                contentStyle={{ backgroundColor: "#18181b", border: "1px solid #27272a", borderRadius: 2, fontFamily: "JetBrains Mono", fontSize: 11 }}
                labelFormatter={(v) => new Date(v).toLocaleDateString()}
                formatter={(v, name, entry) => {
                  if (name === "realized_baseline") {
                    // Iter 79 — swap the shifted-by-startingCash value
                    // shown in the tooltip back to the raw realized_pl
                    // number so admins see the actual booked P/L.
                    const raw = entry?.payload?.realized_pl ?? 0;
                    const sign = raw >= 0 ? "+" : "";
                    return [`${sign}${fmtMoney(raw)}`, "Realized P/L"];
                  }
                  // Iter 82 — bot comparison lines.
                  if (name === "bot1_baseline" || name === "bot2_baseline") {
                    const idx = name === "bot1_baseline" ? 0 : 1;
                    const raw = entry?.payload?.[`bot${idx + 1}_cum`] ?? 0;
                    const sign = raw >= 0 ? "+" : "";
                    const label = perBotSeries[idx]?.bot_name || `Bot ${idx + 1}`;
                    return [`${sign}${fmtMoney(raw)}`, label];
                  }
                  const label = name === "equity" ? "Portfolio" : name === "spy" ? "S&P 500" : name;
                  return [v != null ? fmtMoney(v) : "—", label];
                }}
              />
              <ReferenceLine y={startingCash} stroke="#3f3f46" strokeDasharray="2 3" />
              <Area type="monotone" dataKey="equity" name="equity" stroke={color} strokeWidth={1.5}
                fill="url(#pfill)" dot={false} isAnimationActive={false} />
              {showSP && (
                <Line type="monotone" dataKey="spy" name="spy" stroke="#a1a1aa" strokeWidth={1}
                  strokeDasharray="3 3" dot={false} isAnimationActive={false} />
              )}
              {showRealized && (
                <Line
                  type="monotone"
                  dataKey="realized_baseline"
                  name="realized_baseline"
                  stroke="#FBBF24"
                  strokeWidth={1.25}
                  dot={false}
                  isAnimationActive={false}
                />
              )}
              {showCompare && perBotSeries.length >= 1 && (
                <Line
                  type="monotone"
                  dataKey="bot1_baseline"
                  name="bot1_baseline"
                  stroke="#FBBF24"
                  strokeWidth={1.25}
                  strokeDasharray="6 2"
                  dot={false}
                  isAnimationActive={false}
                />
              )}
              {showCompare && perBotSeries.length >= 2 && (
                <Line
                  type="monotone"
                  dataKey="bot2_baseline"
                  name="bot2_baseline"
                  stroke="#A78BFA"
                  strokeWidth={1.25}
                  strokeDasharray="6 2"
                  dot={false}
                  isAnimationActive={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
